import { useCallback } from 'react';
import { t } from '../i18n';
import { buildMapJson } from '../utils/mapJson';
import { calculateNodePosition } from '../utils/canvas';
import { getDefaultColor } from '../utils/colors';
import { normalizeNodeFlashcard } from '../utils/storage';

const ACTION_ORDER = { add: 0, connect: 1, update: 2 };
const DEFAULT_MAP_NAMES = ['Nuova Mappa', 'La mia prima mappa'];
const GENERIC_ROOT_TITLES = new Set([
  'concetto principale',
  'tema centrale',
  'argomento',
  'argomento principale',
  'problema',
  'nuovo problema',
  "piano d'azione",
  'piano di azione',
  'root',
  'nodo root',
  'nodo centrale',
  'e se...?',
  'e se...',
  // English template placeholders
  'main concept',
  'what if...?',
  'what if...',
]);
const WEAK_TOPIC_PHRASES = new Set([
  'continua',
  'continuiamo',
  'vai',
  'prosegui',
  'avanti',
  'ok',
  'ok grazie',
  'approfondisci',
  'espandi',
  'dettaglia',
  'altro',
  'next'
]);
const TEXT_STOPWORDS = new Set([
  'il', 'lo', 'la', 'i', 'gli', 'le', 'un', 'uno', 'una', 'di', 'del', 'della', 'dei', 'degli', 'delle',
  'a', 'ad', 'da', 'in', 'con', 'su', 'sul', 'sulla', 'sui', 'sugli', 'sulle', 'per', 'tra', 'fra', 'e', 'o'
]);

const cleanTitle = (value = '') => value.trim().replace(/\s+/g, ' ');
const normalizeTitle = (value = '') =>
  cleanTitle(value)
    .toLowerCase()
    .replace(/[!?.,;:()[\]{}"']/g, '');

const isGenericRootTitle = (value = '') => GENERIC_ROOT_TITLES.has(normalizeTitle(value));

const compactToFiveWords = (value = '') => cleanTitle(value).split(' ').filter(Boolean).slice(0, 5).join(' ');
const stemToken = (token = '') => token.replace(/s$/i, '').replace(/[aeiou]$/i, '');
const tokenizeText = (value = '') =>
  normalizeTitle(value)
    .split(/\s+/)
    .filter(Boolean)
    .filter(t => t.length > 2 && !TEXT_STOPWORDS.has(t))
    .map(stemToken);
const uniqueTokens = (value = '') => [...new Set(tokenizeText(value))];
const countOverlap = (a = [], b = []) => a.filter(t => b.includes(t)).length;
const isWeakTopic = (value = '') => {
  const normalized = normalizeTitle(value);
  return !normalized || WEAK_TOPIC_PHRASES.has(normalized);
};

const extractTopicFromUserMessage = (message = '') => {
  const plain = cleanTitle(message.replace(/[`*_#>]/g, ''));
  if (!plain) return '';

  const sentence = plain.split(/\n|[.!?]/)[0].trim();
  const candidate = sentence.includes(':') ? sentence.split(':').slice(1).join(':').trim() : sentence;

  const mapTopic = candidate.match(
    /\bmappa(?:\s+concettuale)?(?:\s+(?:su|sul|sulla|sui|sugli|sulle|di|del|della|dei|degli|delle))\s+(.+)/i
  );
  if (mapTopic?.[1]) return compactToFiveWords(mapTopic[1]);

  const looseTopic = candidate.match(/\b(?:su|sul|sulla|sui|sugli|sulle|di|del|della|dei|degli|delle)\s+(.+)/i);
  if (looseTopic?.[1]) return compactToFiveWords(looseTopic[1]);

  return compactToFiveWords(candidate);
};

const extractTopicFromConversation = (messages = [], currentUserMessage = '') => {
  const userMessages = [currentUserMessage, ...messages.filter(m => m?.role === 'user').map(m => m.content).reverse()];
  for (const text of userMessages) {
    const candidate = extractTopicFromUserMessage(text || '');
    if (!candidate || isWeakTopic(candidate) || isGenericRootTitle(candidate)) continue;
    return candidate;
  }
  return '';
};

const pickAutoRootTitle = ({ currentTitle = '', userMessage = '', mapName = '', messages = [] }) => {
  if (!isGenericRootTitle(currentTitle)) return currentTitle;
  const validMapName = mapName && !DEFAULT_MAP_NAMES.includes(mapName) && !isGenericRootTitle(mapName) ? compactToFiveWords(mapName) : '';
  const fromConversation = extractTopicFromConversation(messages, userMessage);
  const nextTitle = validMapName || fromConversation;
  return nextTitle || currentTitle;
};

const findMentionedParentInMessage = (message = '', candidates = []) => {
  const msg = normalizeTitle(message);
  if (!msg) return null;
  const matches = candidates
    .map(node => ({ node, title: normalizeTitle(node.title) }))
    .filter(entry => entry.title && entry.title.length >= 4 && msg.includes(entry.title))
    .sort((a, b) => b.title.length - a.title.length);
  return matches[0]?.node || null;
};

const findSemanticParent = ({ op, currentNodes, userMessage, rootId }) => {
  const opTokens = uniqueTokens(`${op.title || ''} ${op.summary || ''} ${userMessage || ''}`);
  if (opTokens.length === 0) return null;

  const candidates = currentNodes.filter(n => n.type === 'solution' && n.id !== rootId && !n.banned);
  let best = null;
  let bestScore = 0;

  for (const node of candidates) {
    const nodeTokens = uniqueTokens(`${node.title || ''} ${node.summary || ''}`);
    const overlap = countOverlap(opTokens, nodeTokens);
    const mentionBonus = normalizeTitle(userMessage).includes(normalizeTitle(node.title)) ? 2 : 0;
    const score = overlap * 2 + mentionBonus;
    if (score > bestScore) {
      best = node;
      bestScore = score;
    }
  }

  return bestScore >= 2 ? best : null;
};

const sortOpsByDependency = (ops) => {
  const tempIdSet = new Set(ops.filter(o => o.tempId).map(o => o.tempId));
  return [...ops].sort((a, b) => {
    if (a.type === 'problem' && b.type !== 'problem') return -1;
    if (a.type !== 'problem' && b.type === 'problem') return 1;
    if (a.tempId && b.parentId === a.tempId) return -1;
    if (b.tempId && a.parentId === b.tempId) return 1;
    if (a.parentId && tempIdSet.has(a.parentId) && !b.parentId) return 1;
    const aOrder = ACTION_ORDER[a.action] ?? 99;
    const bOrder = ACTION_ORDER[b.action] ?? 99;
    return aOrder - bOrder;
  });
};

export function useSendMessage({
  input, isLoading, infoGathering, nodes, connections, messages,
  sendConversationalMessage, currentMapName, renameCurrentMap,
  exchangesWithoutNodes, setExchangesWithoutNodes,
  setMessages, setInput, setSuggestions, setIsLoading, setNodes, setConnections,
  currentMode,
  userContext,
  mapContext,
  setLoadingStatus,
  lang = 'en',
}) {
  const handleSendMessage = useCallback(async (overrideMessage) => {
    const userMessage = (typeof overrideMessage === 'string' ? overrideMessage : input).trim();
    if (!userMessage || isLoading) return;

    // ========== INFO GATHERING MODE ==========
    if (infoGathering.isInfoGatheringActive) {
      setSuggestions([]);
      if (infoGathering.checkStopCommand(userMessage)) {
        setMessages(prev => [...prev, { role: 'user', content: userMessage }]);
        setInput('');
        await infoGathering.stopInfoGathering();
        return;
      }

      setMessages(prev => [...prev, { role: 'user', content: userMessage }]);
      setInput('');
      await infoGathering.answerQuestion(userMessage);
      return;
    }

    // ========== NORMAL MODE ==========
    setMessages(prev => [...prev, { role: 'user', content: userMessage }]);
    if (!overrideMessage) setInput('');
    setSuggestions([]);
    setIsLoading(true);
    setLoadingStatus(t('loadingStatus.analyzingContext', lang));

    try {
      const existingRoot = nodes.find(n => n.type === 'problem');
      let nodesForAI = nodes;
      if (existingRoot) {
        const preAutoRootTitle = pickAutoRootTitle({
          currentTitle: existingRoot.title,
          userMessage,
          mapName: currentMapName,
          messages,
        });
        if (preAutoRootTitle && preAutoRootTitle !== existingRoot.title) {
          nodesForAI = nodes.map(n => n.id === existingRoot.id ? { ...n, title: preAutoRootTitle } : n);
          setNodes(nodesForAI);
        }
      }

      const { json: mapJson, idMap, isEmpty } = buildMapJson(nodesForAI, connections);
      
      setTimeout(() => setLoadingStatus(t('loadingStatus.findingConnections', lang)), 1500);

      const isUnnamed = DEFAULT_MAP_NAMES.includes(currentMapName);
      const callTimeout = new Promise((_, reject) =>
        setTimeout(() => reject(new Error('Risposta troppo lenta — riprova.')), 45_000)
      );

      // Implement sliding window: send only the last 10 messages to keep context lean
      const maxHistory = 10;
      const messageHistory = messages.length > maxHistory 
        ? messages.slice(-maxHistory) 
        : messages;

      const { reply, ops, suggestions: newSuggestions, mapName } = await Promise.race([
        sendConversationalMessage(userMessage, mapJson, messageHistory, exchangesWithoutNodes, isUnnamed, isEmpty, currentMode, userContext, mapContext),
        callTimeout,
      ]);

      if (ops.length > 0) setLoadingStatus(t('loadingStatus.drawingSolutions', lang));
      else setLoadingStatus(t('loadingStatus.elaboratingAnswer', lang));

      setSuggestions(newSuggestions || []);
      if (mapName && isUnnamed) renameCurrentMap(mapName);
      setMessages(prev => [...prev, { role: 'assistant', content: reply || '...' }]);

      if (existingRoot) {
        const autoRootTitle = pickAutoRootTitle({
          currentTitle: existingRoot.title,
          userMessage,
          mapName,
          messages,
        });
        if (autoRootTitle && autoRootTitle !== existingRoot.title) {
          setNodes(prev => prev.map(n => n.id === existingRoot.id ? { ...n, title: autoRootTitle } : n));
        }
      }

      if (ops.length > 0) {
        const sortedOps = sortOpsByDependency(ops);

        let currentNodes = [...nodesForAI];
        if (existingRoot) {
          const autoRootTitle = pickAutoRootTitle({
            currentTitle: existingRoot.title,
            userMessage,
            mapName,
            messages,
          });
          if (autoRootTitle && autoRootTitle !== existingRoot.title) {
            currentNodes = currentNodes.map(n => n.id === existingRoot.id ? { ...n, title: autoRootTitle } : n);
          }
        }
        const nodesToAdd = [];
        const connectionsToAdd = [];
        const tempIdMap = {}; // tempId → new internal id (for intra-batch chains)

        for (let i = 0; i < sortedOps.length; i++) {
          let op = sortedOps[i];
          if (op.action !== 'add') continue;
          if (!op.title || !['problem', 'solution'].includes(op.type)) continue;

          const titleNorm = op.title.trim().toLowerCase();
          const isMultiRoot = mapContext?.includes('MULTI_ROOT:true');

          // In multi-root maps, same-titled nodes (e.g. "Backend / Logica") are allowed
          // under different feature roots — skip dedup when the intended parent is a problem node.
          const intendedParentIsProblem = isMultiRoot && op.parentId && (
            (idMap[op.parentId] && currentNodes.find(n => n.id === idMap[op.parentId])?.type === 'problem') ||
            (tempIdMap[op.parentId] && nodesToAdd.find(n => n.id === tempIdMap[op.parentId])?.type === 'problem')
          );

          if (!intendedParentIsProblem) {
            if (currentNodes.some(n => n.title.toLowerCase() === titleNorm)) continue;
            if (nodesToAdd.some(n => n.title.toLowerCase() === titleNorm)) continue;
          }

          // Only one root problem allowed — downgrade extras to solution.
          // Exception: maps with MULTI_ROOT:true in mapContext (e.g. feature-chain template).
          if (op.type === 'problem') {
            const alreadyHasProblem = currentNodes.some(n => n.type === 'problem')
              || nodesToAdd.some(n => n.type === 'problem');
            if (alreadyHasProblem && !isMultiRoot) {
              op = { ...op, type: 'solution' };
            } else if (!alreadyHasProblem) {
              op = {
                ...op,
                title: pickAutoRootTitle({
                  currentTitle: op.title,
                  userMessage,
                  mapName,
                  messages,
                })
              };
            }
          }

          // Resolve parentId → internal node
          let parentNode = null;
          const rootNode = [...currentNodes].reverse().find(n => n.type === 'problem') || null;
          if (op.parentId) {
            // 1. existing node via idMap
            const existingId = idMap[op.parentId];
            if (existingId) parentNode = currentNodes.find(n => n.id === existingId) || null;
            // 2. node added in this batch via tempId
            if (!parentNode && tempIdMap[op.parentId]) {
              parentNode = nodesToAdd.find(n => n.id === tempIdMap[op.parentId]) || null;
            }
          }
          // 3. fallback: selected solution or nearest problem
          // Only try to find a better parent if currently rooted — but never override
          // an explicit problem-node parent (intentional feature-root assignment in multi-root maps).
          const parentIsProblem = parentNode?.type === 'problem';
          if (op.type === 'solution' && !parentIsProblem && (!parentNode || parentNode.id === rootNode?.id)) {
            const solutionCandidates = currentNodes.filter(n => n.type === 'solution' && n.id !== rootNode?.id);
            const mentionedParent = findMentionedParentInMessage(userMessage, solutionCandidates);
            if (mentionedParent) parentNode = mentionedParent;
          }
          if (op.type === 'solution' && !parentIsProblem && (!parentNode || parentNode.id === rootNode?.id)) {
            const semanticParent = findSemanticParent({
              op,
              currentNodes,
              userMessage,
              rootId: rootNode?.id
            });
            if (semanticParent) parentNode = semanticParent;
          }
          if (!parentNode && op.type === 'solution') {
            parentNode = [...currentNodes].reverse().find(n => n.type === 'problem') || null;
          }

          const pos = calculateNodePosition(
            op.type, currentNodes, [...connections, ...connectionsToAdd], parentNode
          );
          const newId = `${Date.now()}-${i}`;
          if (op.tempId) tempIdMap[op.tempId] = newId;

          const newNode = normalizeNodeFlashcard({
            id: newId,
            type: op.type,
            title: op.title.trim(),
            summary: (op.summary || '').trim(),
            description: '',
            ...pos,
            banned: false,
            color: getDefaultColor(op.type),
            flashcardData: op.flashcardData || null,
            _hidden: true,
            _isGhost: true // Mark as ghost during creation
          });
          nodesToAdd.push(newNode);
          currentNodes = [...currentNodes, newNode];

          if (parentNode) {
            connectionsToAdd.push({ id: `${parentNode.id}-${newId}`, from: parentNode.id, to: newId, _animateIn: true });
          }
        }

        // Safety net: connect any orphaned solution to the nearest problem
        for (const newNode of nodesToAdd) {
          if (newNode.type !== 'solution') continue;
          const connected = connectionsToAdd.some(c => c.to === newNode.id)
            || connections.some(c => c.to === newNode.id);
          if (!connected) {
            const problem = [...nodesForAI, ...nodesToAdd].reverse().find(n => n.type === 'problem');
            if (problem) connectionsToAdd.push({ id: `${problem.id}-${newNode.id}`, from: problem.id, to: newNode.id, _animateIn: true });
          }
        }

        // Multi-root: auto-scaffold new problem nodes that are missing standard children.
        // Pattern is copied from the first problem node's direct children, so user
        // customizations to Feature 1 propagate to all subsequent features.
        if (mapContext?.includes('MULTI_ROOT:true')) {
          const newProblemNodes = nodesToAdd.filter(n => n.type === 'problem');
          if (newProblemNodes.length > 0) {
            const firstProblem = nodesForAI.find(n => n.type === 'problem');
            const scaffoldTitles = firstProblem
              ? connections
                  .filter(c => c.from === firstProblem.id)
                  .map(c => nodesForAI.find(n => n.id === c.to))
                  .filter(n => n?.type === 'solution')
                  .map(n => n.title)
              : [];
            const FALLBACK_SCAFFOLDS = ['Backend / Logica', 'UI / Flusso', 'Dipendenze'];
            const finalScaffolds = scaffoldTitles.length > 0 ? scaffoldTitles : FALLBACK_SCAFFOLDS;

            for (const probNode of newProblemNodes) {
              const existingChildTitles = new Set(
                connectionsToAdd
                  .filter(c => c.from === probNode.id)
                  .map(c => [...nodesToAdd, ...currentNodes].find(n => n.id === c.to)?.title?.toLowerCase())
                  .filter(Boolean)
              );
              for (const title of finalScaffolds) {
                if (existingChildTitles.has(title.toLowerCase())) continue;
                const pos = calculateNodePosition('solution', currentNodes, [...connections, ...connectionsToAdd], probNode);
                const sid = `sc-${Date.now()}-${Math.random().toString(36).substr(2, 4)}`;
                const sNode = normalizeNodeFlashcard({
                  id: sid, type: 'solution', title, summary: '', description: '',
                  ...pos, banned: false, color: getDefaultColor('solution'),
                  flashcardData: null, _hidden: true, _isGhost: true,
                });
                nodesToAdd.push(sNode);
                currentNodes = [...currentNodes, sNode];
                connectionsToAdd.push({ id: `${probNode.id}-${sid}`, from: probNode.id, to: sid, _animateIn: true });
              }
            }
          }
        }

        setNodes(prev => [...prev, ...nodesToAdd]);
        setConnections(prev => [...prev, ...connectionsToAdd]);

        // ---- connect ----
        const connectionsToAdd2 = [];
        for (const op of sortedOps) {
          if (op.action !== 'connect') continue;
          if (!op.from || !op.to) continue;

          const fromInternalId = idMap[op.from] || (tempIdMap[op.from] ? tempIdMap[op.from] : null);
          const toInternalId   = idMap[op.to]   || (tempIdMap[op.to]   ? tempIdMap[op.to]   : null);
          if (!fromInternalId || !toInternalId) continue;
          if (fromInternalId === toInternalId) continue;

          const connId = `${fromInternalId}-${toInternalId}`;
          const alreadyExists = connections.some(c => c.id === connId)
            || connectionsToAdd.some(c => c.id === connId)
            || connectionsToAdd2.some(c => c.id === connId);
          if (alreadyExists) continue;

          connectionsToAdd2.push({ id: connId, from: fromInternalId, to: toInternalId, _animateIn: true });
        }
        if (connectionsToAdd2.length > 0) {
          setConnections(prev => [...prev, ...connectionsToAdd2]);
        }

        // ---- update ----
        for (const op of sortedOps) {
          if (op.action !== 'update') continue;
          if (!op.nodeId) continue;

          const internalId = idMap[op.nodeId] || (tempIdMap[op.nodeId] ? tempIdMap[op.nodeId] : null);
          if (!internalId) continue;

          const updates = {};
          if (op.title) {
            const currentNode = currentNodes.find(n => n.id === internalId);
            const nextTitle = currentNode?.type === 'problem'
              ? pickAutoRootTitle({ currentTitle: op.title, userMessage, mapName, messages })
              : op.title;
            updates.title = nextTitle.trim();
          }
          if (op.summary) updates.summary = op.summary.trim();
          if (Object.keys(updates).length === 0) continue;

          setNodes(prev => prev.map(n => {
            if (n.id !== internalId) return n;
            return normalizeNodeFlashcard({ ...n, ...updates });
          }));
        }

        // ---- move ----
        for (const op of sortedOps) {
          if (op.action !== 'move') continue;
          if (!op.nodeId || !op.parentId) continue;

          const nodeId = idMap[op.nodeId];
          const parentId = idMap[op.parentId];
          if (!nodeId || !parentId || nodeId === parentId) continue;

          setConnections(prev => {
            // Rimuovi vecchie connessioni dove il nodo era figlio
            const filtered = prev.filter(c => c.to !== nodeId);
            // Aggiungi la nuova connessione gerarchica
            return [...filtered, { id: `${parentId}-${nodeId}`, from: parentId, to: nodeId, _animateIn: true }];
          });
        }

        for (let i = 0; i < nodesToAdd.length; i++) {
          await new Promise(r => setTimeout(r, i === 0 ? 100 : 350));
          const nodeId = nodesToAdd[i].id;
          // Transition from ghost to real node
          setNodes(prev => prev.map(n => n.id === nodeId ? { ...n, _hidden: false, _isGhost: false } : n));
        }

        setExchangesWithoutNodes(0);
      } else {
        setExchangesWithoutNodes(prev => prev + 1);
      }
    } catch (error) {
      if (error.message === 'DAILY_CAP_EXCEEDED') {
        setMessages(prev => [...prev, {
          role: 'assistant',
          content: 'Hai esaurito i 2€ di AI gratuita per oggi. Torna domani — o inserisci la tua API key per continuare.'
        }]);
      } else {
        setMessages(prev => [...prev, {
          role: 'assistant',
          content: `Qualcosa è andato storto: ${error.message}`
        }]);
      }
    } finally {
      setIsLoading(false);
      setLoadingStatus('');
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [input, isLoading, infoGathering, nodes, connections, messages, sendConversationalMessage, currentMapName, renameCurrentMap, exchangesWithoutNodes, setExchangesWithoutNodes, currentMode, userContext, mapContext, setLoadingStatus]);

  return { handleSendMessage };
}
